import { useState } from "react";
import {
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid
} from "@mui/material";
import { CaseWhenBenefit, useSubmitReimbursementRequestMutation, WhenBenefitsDocument } from "services/GraphQl";
import moment from "moment";
import { useAuth } from "contexts/User";
import { useNotifications } from "contexts/Notification";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";
import { useForm, Controller } from "react-hook-form";
import { validateReimbursementRequest, getMonthRange } from "../utils";
import { useFileHandling } from "../hooks";
import ReimbursementFileSection from "./ReimbursementFileSection";
import SubmitReimbursementHeader from "./shared/SubmitReimbursementHeader";
import ReimbursementConfirmation from "./shared/ReimbursementConfirmation";

interface ReimbursementFormData {
  amount: number;
  confirmation: boolean;
  understanding: boolean;
  coverageMonth: string;
}

function SubmitLumpSumReimbursementRequest({
  hasBankAccount,
  whenBenefit,
  companyId,
  caseId,
  onSave,
  onCancel,
}: {
  hasBankAccount: boolean;
  whenBenefit: CaseWhenBenefit;
  companyId: string;
  caseId: string;
  onSave: Function;
  onCancel: Function;
}) {
  const { user } = useAuth();
  const [submitReimbursementRequest] = useSubmitReimbursementRequestMutation({
    refetchQueries: [WhenBenefitsDocument],
    awaitRefetchQueries: true,
  });
  const { notifications } = useNotifications();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    files,
    fileError,
    uploading,
    uploadFile,
    photo,
    setPhoto,
    facingMode,
    setFacingMode,
    webcamRef,
    takePhoto,
    uploadFiles,
    deleteFile,
  } = useFileHandling();

  const amountRemaining = whenBenefit.detail?.amountRemaining || 0;

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ReimbursementFormData>({
    defaultValues: {
      amount: 0,
      confirmation: false,
      understanding: false,
      coverageMonth: "",
    },
  });

  // Generate coverage month options: 3 months before current, current, and 12 months after
  const coverageMonthOptions = getMonthRange(3, 12);

  const onSubmit = async (data: ReimbursementFormData) => {
    if (isSubmitting || !user) return;

    if (!validateReimbursementRequest(data, files, amountRemaining, hasBankAccount, notifications)) {
      return;
    }

    if (!data.coverageMonth) {
      notifications.error("Please select a coverage month.");
      return;
    }

    try {
      setIsSubmitting(true);
      await submitReimbursementRequest({
        variables: {
          whenBenefitId: (whenBenefit as any).id || "",
          files,
          amount: data.amount,
          months: [data.coverageMonth],
          userId: user.uid,
          companyId,
          caseId,
        },
      });

      notifications.success(
        `Reimbursement request of ${data.amount.toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })} submitted successfully.`
      );
      onSave();
    } catch (error) {
      console.error("Error submitting reimbursement request:", error);
      notifications.error("An error occurred while submitting the request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!whenBenefit) {
    return null;
  }

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 4,
      }}
    >
      <SubmitReimbursementHeader />
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="amount"
            control={control}
            rules={{
              required: "Amount is required",
              min: { value: 0.01, message: "Amount must be greater than 0" },
              max: { value: amountRemaining, message: "Amount cannot exceed remaining benefit" },
            }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Amount Requested"
                data-testid="amount-requested"
                variant="outlined"
                fullWidth
                type="number"
                error={!!errors.amount}
                helperText={errors.amount?.message}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  inputProps: {
                    step: "0.01",
                    pattern: "\\d*\\.?\\d{0,2}",
                  },
                }}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  field.onChange(isNaN(value) ? 0 : value);
                }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Typography
            variant="body1"
            sx={{
              pb: { xs: 0, sm: 2 },
              paddingLeft: { xs: 0, sm: 2 },
              paddingTop: { xs: 1, sm: 0 },
            }}
          >
            Remaining Benefit: <FormattedCurrency value={amountRemaining} />
          </Typography>
        </Grid>
      </Grid>

      <Controller
        name="coverageMonth"
        control={control}
        rules={{ required: "Please select a coverage month" }}
        render={({ field }) => (
          <FormControl fullWidth margin="normal">
            <InputLabel id="coverage-month-label">Coverage Month</InputLabel>
            <Select
              {...field}
              labelId="coverage-month-label"
              label="Coverage Month"
            >
              {coverageMonthOptions.map((month) => (
                <MenuItem key={month} value={month}>
                  {moment(month, "YYYY-MM").format("MMMM YYYY")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      />

      <ReimbursementFileSection
        files={files}
        fileError={fileError}
        uploading={uploading}
        uploadFile={uploadFile}
        photo={photo}
        setPhoto={setPhoto}
        facingMode={facingMode}
        setFacingMode={setFacingMode}
        webcamRef={webcamRef}
        takePhoto={takePhoto}
        uploadFiles={uploadFiles}
        deleteFile={deleteFile}
      />

      <ReimbursementConfirmation control={control} errors={errors} hasBankAccount={hasBankAccount} />

      <Box sx={{ display: "flex", flexDirection: "row", gap: 2, alignItems: "center" }}>
        <Button
          type="submit"
          sx={{ fontSize: { xs: "0.675rem", sm: "0.875rem" } }}
          variant="contained"
          color="secondary"
          data-testid="submitReimbursementButton"
          disabled={isSubmitting}
        >
          Submit Reimbursement Request
        </Button>
        <Button
          sx={{ fontSize: { xs: "0.675rem", sm: "0.875rem" } }}
          variant="contained"
          color="secondary"
          onClick={() => onCancel()}
        >
          Cancel
        </Button>
      </Box>
    </Box>
  );
}

export default SubmitLumpSumReimbursementRequest; 